import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import Toast from '@/components/base/Toast';
import { useFavorites } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { getMovieById, getRecommendations } from '@/utils/movies';

const playModes = [
  { icon: 'ri-4k-fill', label: '蓝光 4K', desc: '超高清画质' },
  { icon: 'ri-hd-fill', label: '超清 1080P', desc: '流畅不卡顿' },
  { icon: 'ri-speed-up-line', label: '流畅模式', desc: '省流量观看' },
];

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useSeo({
    title: movie
      ? `${movie.title} - ${movie.type === 'movie' ? '电影' : '电视剧'}详情 - 香蕉视频app`
      : '影视详情 - 香蕉视频app',
    description: movie ? movie.summary.slice(0, 110) : '香蕉视频app 影视详情页',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},香蕉视频app,高清影视` : '香蕉视频app',
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-600"></i>
        <h1 className="mt-5 font-heading font-bold text-xl text-foreground-50">影视资源不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">该作品可能已下架，去首页看看其他精彩内容吧</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const recommendations = getRecommendations(movie, 6);
  const typeLabel = movie.type === 'movie' ? '电影' : '电视剧';

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        showToast('链接已复制，快分享给好友吧');
      } else {
        showToast('分享链接：' + url);
      }
    } catch {
      showToast('分享失败，请手动复制地址栏链接');
    }
  };

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(movie.id);
    showToast(nowFavorited ? '已加入我的收藏（本地演示）' : '已取消收藏');
  };

  return (
    <div className="w-full">
      {/* backdrop */}
      <div className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
        <img
          src={movie.poster}
          alt={`${movie.title}背景`}
          className="w-full h-full object-cover object-top scale-110 blur-sm opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/80 to-background-50"></div>
      </div>

      <div className="w-full px-4 md:px-8 -mt-[220px] md:-mt-[300px] relative z-10">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${movie.type}`} className="hover:text-primary-400 transition-colors cursor-pointer">{typeLabel}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">{movie.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-10">
          {/* poster */}
          <div className="w-[200px] md:w-[260px] lg:w-full mx-auto lg:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title}海报`}
                title={`${movie.title} - 高清${typeLabel}在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="rounded-md bg-background-100 border border-background-200 py-2 text-center">
                <p className="font-heading font-extrabold text-lg text-primary-400">{movie.rating.toFixed(1)}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">评分</p>
              </div>
              <div className="rounded-md bg-background-100 border border-background-200 py-2 text-center">
                <p className="font-heading font-extrabold text-lg text-foreground-100">{movie.year}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">年代</p>
              </div>
              <div className="rounded-md bg-background-100 border border-background-200 py-2 text-center">
                <p className="font-heading font-extrabold text-lg text-foreground-100">{typeLabel}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">类型</p>
              </div>
            </div>
          </div>

          {/* info */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {movie.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-md text-xs bg-accent-950/70 text-accent-300 border border-accent-800/50">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-50">{movie.title}</h1>
            <p className="mt-2 text-sm md:text-base text-foreground-400">{movie.subtitle}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-300">
              <span className="flex items-center gap-1 text-primary-400 font-semibold">
                <i className="ri-star-fill"></i>{movie.rating.toFixed(1)}
              </span>
              <span>{movie.duration}</span>
              <span>{movie.language}</span>
              <span className="flex items-center gap-1 text-foreground-500">
                <i className="ri-eye-line"></i>{movie.playCount} 万次播放
              </span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category/${movie.type}?genre=${encodeURIComponent(genre)}`}
                  className="px-3 py-1.5 rounded-full text-xs md:text-sm bg-secondary-900/60 text-secondary-100 border border-secondary-700/50 hover:bg-secondary-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => showToast('播放功能待接入，敬请期待')}
                className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-xl"></i>
                立即播放
              </button>
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 h-12 px-6 rounded-md border text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? 'border-primary-500 bg-primary-950/50 text-primary-300'
                    : 'border-foreground-600 text-foreground-100 hover:bg-background-200/70'
                }`}
              >
                <i className={favorited ? 'ri-bookmark-fill text-lg' : 'ri-bookmark-line text-lg'}></i>
                {favorited ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-foreground-600 text-foreground-100 text-sm font-semibold hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line text-lg"></i>
                分享
              </button>
            </div>

            {/* meta */}
            <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">导演</dt>
                <dd className="text-foreground-100 text-sm">{movie.director}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">主演</dt>
                <dd className="text-foreground-100 text-sm">{movie.cast.join(' / ')}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">地区</dt>
                <dd className="text-foreground-100 text-sm">{movie.region}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">语言</dt>
                <dd className="text-foreground-100 text-sm">{movie.language}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">年代</dt>
                <dd className="text-foreground-100 text-sm">{movie.year}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="text-foreground-500 text-sm shrink-0 w-16">更新</dt>
                <dd className="text-foreground-100 text-sm">{movie.updatedAt}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* summary */}
        <section className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">剧情简介</h2>
          </div>
          <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-4xl">{movie.summary}</p>
        </section>

        {/* play modes */}
        <section className="mt-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">播放模式</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            {playModes.map((mode) => (
              <div key={mode.label} className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="w-11 h-11 rounded-lg bg-primary-950/60 text-primary-400 flex items-center justify-center shrink-0">
                  <i className={`${mode.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-100">{mode.label}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{mode.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs md:text-sm text-foreground-500 flex items-center gap-2">
            <i className="ri-information-line text-accent-400"></i>
            温馨提示：本站为影视信息展示平台，播放功能待接入，以上模式仅作界演示。
          </p>
        </section>

        {/* related */}
        <section className="mt-12 pb-4">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
            <h2 className="font-heading font-bold text-lg md:text-2xl text-foreground-50">相关推荐</h2>
          </div>
          <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {recommendations.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>

      <Toast message={toast} visible={Boolean(toast)} />
    </div>
  );
}